/**
 * Column picker — choose which record-metadata, form fields, and calculated
 * fields appear as columns. Repeatable_array fields expose their collapse mode
 * inline. Edits spec.columns.
 */
import { Columns3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select'
import { META_COLUMNS, type ViewColumn, type ArrayMode } from '../types/data-view-spec'
import type { FormCatalog } from '../lib/column-catalog'

const ARRAY_MODES: { value: ArrayMode; label: string }[] = [
  { value: 'join', label: 'فهرست (به‌هم‌پیوسته)' },
  { value: 'aggregate', label: 'تعداد/جمع' },
  { value: 'separate', label: 'نمایش تعداد' },
]

interface ColumnPickerProps {
  catalog: FormCatalog
  formId: string
  columns: ViewColumn[]
  onChange: (cols: ViewColumn[]) => void
}

export function ColumnPicker({ catalog, formId, columns, onChange }: ColumnPickerProps) {
  const has = (source: ViewColumn['source'], key: string) =>
    columns.some((c) => c.source === source && c.key === key)
  const find = (source: ViewColumn['source'], key: string) =>
    columns.find((c) => c.source === source && c.key === key)

  const toggle = (col: ViewColumn, on: boolean) => {
    if (on) {
      if (has(col.source, col.key)) return
      onChange([...columns, col])
    } else {
      onChange(columns.filter((c) => !(c.source === col.source && c.key === col.key)))
    }
  }

  const updateArray = (key: string, patch: Partial<NonNullable<ViewColumn['array']>>) => {
    onChange(columns.map((c) =>
      c.source === 'field' && c.key === key
        ? { ...c, array: { mode: 'join', ...c.array, ...patch } as ViewColumn['array'] }
        : c,
    ))
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Columns3 className="w-3.5 h-3.5" />
          ستون‌ها
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-h-[70vh] overflow-y-auto p-0">
        <div className="p-3 space-y-3">
          <Group label="اطلاعات رکورد">
            {META_COLUMNS.map((m) => (
              <Row
                key={m.key}
                label={m.label}
                checked={has('record_meta', m.key)}
                onCheck={(on) => toggle({ id: `m_${m.key}`, source: 'record_meta', key: m.key }, on)}
              />
            ))}
          </Group>

          <Group label="فیلدهای فرم">
            {catalog.fields.map((f) => {
              const checked = has('field', f.fieldKey)
              const col = find('field', f.fieldKey)
              return (
                <div key={f.fieldKey}>
                  <Row
                    label={f.label}
                    hint={f.isFileLike ? 'فایل' : undefined}
                    checked={checked}
                    onCheck={(on) => toggle({ id: `f_${f.fieldKey}`, source: 'field', key: f.fieldKey, formId }, on)}
                  />
                  {checked && f.isRepeatableArray && (
                    <div className="ms-6 mb-2 mt-1 space-y-1.5">
                      <Select
                        value={col?.array?.mode ?? 'join'}
                        onValueChange={(v) => updateArray(f.fieldKey, { mode: v as ArrayMode })}
                      >
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ARRAY_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {(col?.array?.mode ?? 'join') !== 'separate' && f.subFields && f.subFields.length > 0 && (
                        <Select
                          value={col?.array?.subFieldKey ?? f.subFields[0].fieldKey}
                          onValueChange={(v) => updateArray(f.fieldKey, { subFieldKey: v })}
                        >
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="زیرفیلد" /></SelectTrigger>
                          <SelectContent>
                            {f.subFields.map((sf) => <SelectItem key={sf.fieldKey} value={sf.fieldKey}>{sf.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
            {catalog.fields.length === 0 && <p className="text-xs text-muted-foreground px-1">فیلدی موجود نیست.</p>}
          </Group>

          {catalog.calculated.length > 0 && (
            <Group label="فیلدهای محاسباتی">
              {catalog.calculated.map((c) => (
                <Row
                  key={c.field_key}
                  label={c.label.fa || c.field_key}
                  checked={has('field', c.field_key)}
                  onCheck={(on) => toggle({ id: `c_${c.field_key}`, source: 'field', key: c.field_key, formId }, on)}
                />
              ))}
            </Group>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-muted-foreground/70 uppercase mb-1">{label}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  )
}

function Row({ label, checked, onCheck, hint }: { label: string; checked: boolean; onCheck: (on: boolean) => void; hint?: string }) {
  return (
    <label className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted cursor-pointer text-sm">
      <Checkbox checked={checked} onCheckedChange={(v) => onCheck(!!v)} />
      <span className="flex-1 truncate">{label}</span>
      {hint && <span className="text-[10px] text-muted-foreground/60">{hint}</span>}
    </label>
  )
}
